import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import LevelForm from '../components/LevelForm';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './Levels.css';

// Componente para item de subnivel arrastrable
const SortableSublevel = ({ sublevel, category, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sublevel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sublevel-item ${!sublevel.is_active ? 'inactive' : ''} ${isDragging ? 'dragging' : ''}`}
    >
      <span className="drag-handle" {...attributes} {...listeners}>
        ⋮⋮
      </span>
      <span className="sublevel-order">{sublevel.order}</span>
      <span className="sublevel-name">{sublevel.name}</span>
      <span className={`badge badge-sm ${sublevel.is_active ? 'badge-success' : 'badge-danger'}`}>
        {sublevel.is_active ? 'Activo' : 'Inactivo'}
      </span>
      <button 
        className="btn btn-link btn-sm"
        onClick={() => onEdit({ ...sublevel, parent: category, discipline_id: category.discipline_id })}
      >
        Editar
      </button>
    </div>
  );
};

const Levels = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [formMode, setFormMode] = useState('level'); // 'level' o 'sublevel'
  const [parentLevelForSublevel, setParentLevelForSublevel] = useState(null);

  // Configurar sensores para drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Obtener disciplinas para el filtro
  const { data: disciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  // Obtener niveles (solo raíz para la vista principal)
  const { data: levels, isLoading, error } = useQuery(
    ['levels', filterDiscipline],
    async () => {
      const params = { only_root: true };
      if (filterDiscipline) params.discipline_id = filterDiscipline;
      const response = await api.get('/levels', { params });
      return response.data;
    },
    {
      onError: (err) => {
        console.error('Error al cargar niveles:', err);
      }
    }
  );

  const createLevelMutation = useMutation(
    (data) => api.post('/levels', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('levels');
        toast.success(formMode === 'sublevel' ? 'Subnivel creado exitosamente!' : 'Nivel creado exitosamente!');
        setIsModalOpen(false);
        setEditingLevel(null);
        setFormMode('level');
        setParentLevelForSublevel(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail.forEach(e => toast.error(`${e.loc[1]}: ${e.msg}`));
        } else {
          toast.error(errorDetail || 'Error al crear nivel');
        }
      },
    }
  );

  const updateLevelMutation = useMutation(
    ({ id, data }) => api.put(`/levels/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('levels');
        toast.success('Nivel actualizado exitosamente!');
        setIsModalOpen(false);
        setEditingLevel(null);
        setFormMode('level');
        setParentLevelForSublevel(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail.forEach(e => toast.error(`${e.loc[1]}: ${e.msg}`));
        } else {
          toast.error(errorDetail || 'Error al actualizar nivel');
        }
      },
    }
  );

  const toggleLevelStatusMutation = useMutation(
    ({ id, is_active }) => api.put(`/levels/${id}`, { is_active }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('levels');
        toast.success('Estado del nivel actualizado!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al cambiar estado');
      },
    }
  );

  // Mutation para actualizar orden de subniveles
  const updateSublevelOrderMutation = useMutation(
    async ({ sublevels }) => {
      // Actualizar orden de cada subnivel
      const promises = sublevels.map((sublevel, index) =>
        api.put(`/levels/${sublevel.id}`, { order: index })
      );
      await Promise.all(promises);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('levels');
        toast.success('Orden actualizado!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al actualizar orden');
      },
    }
  );

  const handleCreateClick = () => {
    setEditingLevel(null);
    setFormMode('level');
    setParentLevelForSublevel(null);
    setIsModalOpen(true);
  };

  const handleCreateSublevelClick = (parentLevel) => {
    setEditingLevel(null);
    setFormMode('sublevel');
    setParentLevelForSublevel(parentLevel);
    setIsModalOpen(true);
  };

  const handleEditClick = (level) => {
    setEditingLevel(level);
    setFormMode(level.parent_level_id ? 'sublevel' : 'level');
    setParentLevelForSublevel(level.parent || null);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingLevel) {
      // Si estamos editando un subnivel, asegurar que parent_level_id se preserve
      if (editingLevel.parent_level_id) {
        data.parent_level_id = editingLevel.parent_level_id;
      }
      // Asegurar que category_type se preserve para subniveles
      if (editingLevel.parent_level_id && !data.category_type) {
        data.category_type = 'skill'; // Subniveles siempre son de habilidad
      }
      updateLevelMutation.mutate({ id: editingLevel.id, data });
    } else {
      createLevelMutation.mutate(data);
    }
  };

  const handleToggleStatus = (level) => {
    const action = level.is_active ? 'desactivar' : 'activar';
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Nivel`,
      message: `¿Estás seguro de ${action} el nivel "${level.name}"?`,
      onConfirm: () => {
        toggleLevelStatusMutation.mutate({
          id: level.id,
          is_active: !level.is_active,
        });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
    });
  };

  // Manejar reordenamiento de subniveles
  const handleDragEnd = (event, categoryId) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Encontrar la categoría y sus subniveles
    const category = levels.find(l => l.id === categoryId);
    if (!category || !category.sublevels) return;

    const oldIndex = category.sublevels.findIndex(s => s.id === active.id);
    const newIndex = category.sublevels.findIndex(s => s.id === over.id);

    const reorderedSublevels = arrayMove(category.sublevels, oldIndex, newIndex);

    // Actualizar orden en el backend
    updateSublevelOrderMutation.mutate({ sublevels: reorderedSublevels });
  };

  // Agrupar niveles por disciplina y ordenar por order
  const levelsByDiscipline = useMemo(() => {
    const grouped = {};
    if (levels) {
      levels.forEach(level => {
        const disciplineId = level.discipline_id;
        if (!grouped[disciplineId]) {
          grouped[disciplineId] = {
            discipline: disciplines?.find(d => d.id === disciplineId),
            categories: [], // Niveles tipo "age" (categorías con subniveles)
            skillLevels: [] // Niveles tipo "skill" sin padre (niveles simples)
          };
        }
        
        if (level.category_type === 'age') {
          grouped[disciplineId].categories.push(level);
        } else {
          grouped[disciplineId].skillLevels.push(level);
        }
      });
      
      // Ordenar categorías y niveles simples por order dentro de cada disciplina
      Object.keys(grouped).forEach(disciplineId => {
        grouped[disciplineId].categories.sort((a, b) => (a.order || 0) - (b.order || 0));
        grouped[disciplineId].skillLevels.sort((a, b) => (a.order || 0) - (b.order || 0));
      });
    }
    return grouped;
  }, [levels, disciplines]);

  // Helper para formatear información de categoría
  const formatCategoryInfo = (level) => {
    const parts = [];
    if (level.min_birth_year || level.max_birth_year) {
      if (level.min_birth_year === level.max_birth_year) {
        parts.push(`Año: ${level.min_birth_year}`);
      } else {
        parts.push(`Año: ${level.min_birth_year || '?'}${level.max_birth_year ? `-${level.max_birth_year}` : '+'}`);
      }
    }
    if (level.min_age || level.max_age) {
      if (level.min_age === level.max_age) {
        parts.push(`Edad: ${level.min_age} años`);
      } else {
        parts.push(`Edad: ${level.min_age || '?'}${level.max_age ? `-${level.max_age}` : '+'} años`);
      }
    }
    return parts.length > 0 ? parts.join(' | ') : null;
  };

  if (isLoading) {
    return <div className="loading">Cargando niveles...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>Error al cargar niveles</h3>
          <p>{error.response?.data?.detail || error.message || 'Error desconocido'}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => queryClient.invalidateQueries('levels')}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Gestión de Niveles y Categorías</h2>
        <button className="btn btn-primary" onClick={handleCreateClick}>
          + Nuevo Nivel/Categoría
        </button>
      </div>

      <div className="card">
        <div className="filters-bar">
          <select
            value={filterDiscipline}
            onChange={(e) => setFilterDiscipline(e.target.value)}
            className="filter-select"
          >
            <option value="">Todas las disciplinas</option>
            {disciplines?.map((discipline) => (
              <option key={discipline.id} value={discipline.id}>
                {discipline.icon || '⚽'} {discipline.name}
              </option>
            ))}
          </select>
        </div>

        {Object.keys(levelsByDiscipline).length > 0 ? (
          <div className="levels-container">
            {Object.entries(levelsByDiscipline).map(([disciplineId, data]) => (
              <div key={disciplineId} className="discipline-section">
                <div className="discipline-header">
                  <h3>
                    {data.discipline?.icon || '⚽'} {data.discipline?.name || 'Disciplina desconocida'}
                  </h3>
                  <span className="level-count">
                    {data.categories.length} categoría(s), {data.skillLevels.length} nivel(es) simple(s)
                  </span>
                </div>

                {/* Categorías por edad (con subniveles) */}
                {data.categories.length > 0 && (
                  <div className="categories-section">
                    <h4 className="section-title">📂 Categorías por Edad</h4>
                    {data.categories
                      .sort((a, b) => a.order - b.order)
                      .map((category) => (
                        <div key={category.id} className={`category-card ${!category.is_active ? 'inactive' : ''}`}>
                          <div className="category-header">
                            <div className="category-info">
                              <span className="order-badge">{category.order}</span>
                              <strong className="category-name">{category.name}</strong>
                              {formatCategoryInfo(category) && (
                                <span className="category-details">{formatCategoryInfo(category)}</span>
                              )}
                              <span className={`badge ${category.is_active ? 'badge-success' : 'badge-danger'}`}>
                                {category.is_active ? 'Activo' : 'Inactivo'}
                              </span>
                            </div>
                            <div className="category-actions">
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => handleCreateSublevelClick(category)}
                              >
                                + Subnivel
                              </button>
                              <button 
                                className="btn btn-secondary btn-sm" 
                                onClick={() => handleEditClick(category)}
                              >
                                Editar
                              </button>
                              <button
                                className={`btn btn-sm ${category.is_active ? 'btn-warning' : 'btn-success'}`}
                                onClick={() => handleToggleStatus(category)}
                                disabled={toggleLevelStatusMutation.isLoading}
                              >
                                {category.is_active ? 'Desactivar' : 'Activar'}
                              </button>
                            </div>
                          </div>

                          {/* Subniveles */}
                          {category.sublevels && category.sublevels.length > 0 ? (
                            <div className="sublevels-container">
                              <span className="sublevels-label">Subniveles de habilidad (arrastra para reordenar):</span>
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(event) => handleDragEnd(event, category.id)}
                              >
                                <SortableContext
                                  items={category.sublevels.map(s => s.id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <div className="sublevels-list">
                                    {category.sublevels
                                      .sort((a, b) => a.order - b.order)
                                      .map((sublevel) => (
                                        <SortableSublevel
                                          key={sublevel.id}
                                          sublevel={sublevel}
                                          category={category}
                                          onEdit={handleEditClick}
                                        />
                                      ))}
                                  </div>
                                </SortableContext>
                              </DndContext>
                            </div>
                          ) : (
                            <div className="no-sublevels">
                              <span>Sin subniveles.</span>
                              <button 
                                className="btn btn-link btn-sm"
                                onClick={() => handleCreateSublevelClick(category)}
                              >
                                Agregar subnivel
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* Niveles simples (sin categoría padre) */}
                {data.skillLevels.length > 0 && (
                  <div className="skill-levels-section">
                    <h4 className="section-title">🎯 Niveles de Habilidad (Sin categoría)</h4>
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th style={{ width: '50px' }}>Orden</th>
                            <th>Nombre</th>
                            <th>Descripción</th>
                            <th>Estado</th>
                            <th style={{ width: '200px' }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.skillLevels
                            .sort((a, b) => a.order - b.order)
                            .map((level) => (
                              <tr key={level.id} className={!level.is_active ? 'inactive-row' : ''}>
                                <td>
                                  <span className="order-badge">{level.order}</span>
                                </td>
                                <td>
                                  <strong>{level.name}</strong>
                                </td>
                                <td>
                                  <span className="description-text">
                                    {level.description || '-'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${level.is_active ? 'badge-success' : 'badge-danger'}`}>
                                    {level.is_active ? 'Activo' : 'Inactivo'}
                                  </span>
                                </td>
                                <td>
                                  <button 
                                    className="btn btn-secondary btn-sm" 
                                    onClick={() => handleEditClick(level)}
                                  >
                                    Editar
                                  </button>
                                  <button
                                    className={`btn btn-sm ${level.is_active ? 'btn-warning' : 'btn-success'}`}
                                    onClick={() => handleToggleStatus(level)}
                                    disabled={toggleLevelStatusMutation.isLoading}
                                  >
                                    {level.is_active ? 'Desactivar' : 'Activar'}
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {data.categories.length === 0 && data.skillLevels.length === 0 && (
                  <div className="empty-discipline">
                    <p>No hay niveles para esta disciplina</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No hay niveles registrados</p>
            {!filterDiscipline && (
              <p className="empty-state-hint">
                Crea categorías por edad o niveles de habilidad para organizar tus grupos
              </p>
            )}
          </div>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          setFormMode('level');
          setParentLevelForSublevel(null);
        }} 
        title={
          editingLevel 
            ? (editingLevel.parent_level_id ? 'Editar Subnivel' : 'Editar Nivel/Categoría')
            : (formMode === 'sublevel' ? 'Nuevo Subnivel' : 'Nuevo Nivel/Categoría')
        }
        closeOnOverlayClick={false}
      >
        <LevelForm
          initialData={editingLevel}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setFormMode('level');
            setParentLevelForSublevel(null);
          }}
          isLoading={createLevelMutation.isLoading || updateLevelMutation.isLoading}
          mode={formMode}
          parentLevel={parentLevelForSublevel}
        />
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      )}
    </div>
  );
};

export default Levels;
